import type { PocInputValidity } from 'commons/types';
import { Select, SelectPropsValue } from 'commons/components';
import { MAX_OPTIONS_WITHOUT_SEARCH } from 'core/constants/constants';
import { useTranslation } from 'core/i18n/i18n';
import React from 'react';
import { OnChangeAutomationRuleStateData } from '../types/automation-rules.type';

export interface AutomationRulesDropdownOptions {
    id?: string;
    label: string;
    value: string;
}

interface AutomationRulesDropdownProps {
    name: string;
    defaultSelectedOption?: SelectPropsValue;
    options: AutomationRulesDropdownOptions[];
    type: string;
    placeholder?: string;
    disabled?: boolean;
    virtualized?: boolean;
    onChange: (data: OnChangeAutomationRuleStateData) => void;
}

const defaultValidation: PocInputValidity = {
    state: 'valid',
};

export const AutomationRulesDropdown = React.memo((props: AutomationRulesDropdownProps) => {
    const { name, defaultSelectedOption, options, placeholder, type, disabled, virtualized, onChange } = props;
    const { t } = useTranslation();
    const [selectedOption, setSelectedOption] = React.useState<SelectPropsValue>(defaultSelectedOption ?? '');
    const [validity, setValidity] = React.useState(structuredClone(defaultValidation));

    const onValueChangeHandler = (field: string, value: SelectPropsValue) => {
        if (value) {
            const firstValue = String(value[0]);
            const data = { key: field, value: firstValue, type, customIdentifier: firstValue };
            onChange?.(data);
            setSelectedOption(firstValue);
        }
    };

    const onClearHandler = () => {
        onChange({ key: name, value: undefined, type, customIdentifier: '' });
        setSelectedOption(undefined);
    };

    const mappedOptions = options.map((option) => ({ text: t(option.label), value: option?.id || option.value }));

    return (
        <Select
            name={name}
            value={selectedOption}
            placeholder={placeholder ?? ''}
            onChange={onValueChangeHandler}
            disabled={disabled}
            onClear={onClearHandler}
            options={mappedOptions}
            searchable={mappedOptions.length > MAX_OPTIONS_WITHOUT_SEARCH}
            clearButton
            validity={validity}
            onValidity={(_field: string, newValidity: PocInputValidity) => {
                setValidity(newValidity);
            }}
            required
            floatingAssistiveText
            virtualized={virtualized}
        />
    );
});
